import { Button, Radiogroup, RadiogroupOptions } from "shared";
import { ComponentTypes, externalFormProtectTypes, ITemplateComponent } from "entities/template";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useComponentsTranslations } from "../../lib/i18n";
import styles from "./external_form_modal.module.scss";

interface IExternalFormModalProps {
    close: () => void;
    component: ITemplateComponent;
    onSave: (component: ITemplateComponent) => void;
}

export const ExternalFormModal: React.FC<IExternalFormModalProps> = React.memo(({
                                                                                    close,
                                                                                    component,
                                                                                    onSave
                                                                                }) => {
    const t = useComponentsTranslations();
    const [radioValue, setRadioValue] = useState<string>(component.protect ? component.protect : "undefined");
    const { i18n } = useTranslation();

    useEffect(() => {
        const isReadOnlyComponent = !!component.readonly;
        if (isReadOnlyComponent && radioValue === "undefined") {
            setRadioValue(externalFormProtectTypes.gray);
        }
    }, [component, radioValue])

    useEffect(() => {
        if (component.type === ComponentTypes.legalYandex && radioValue === externalFormProtectTypes.hide) {
            setRadioValue("undefined");
        }
    }, [component.type, component.id, radioValue])

    const externalFormOptions: RadiogroupOptions[] = useMemo(() => {
        const isReadOnlyComponent = !!component.readonly;
        let options: RadiogroupOptions[] = [{
            label: t("usual"),
            disabled: isReadOnlyComponent,
            value: "undefined"
        }, {
            label: t("readOnly"),
            value: externalFormProtectTypes.gray
        }, {
            label: t("hidden"),
            value: externalFormProtectTypes.hide
        }]

        if ([ComponentTypes.file, ComponentTypes.saveAndTicket, ComponentTypes.externalForm, ComponentTypes.text].includes(component.type)) {
            options = options.filter((o) => o.value !== externalFormProtectTypes.gray);
        }

        if (component.type === ComponentTypes.legalYandex) {
            options = options.filter((o) => o.value !== externalFormProtectTypes.hide);
        }

        return options;
    }, [component, i18n.language])

    const displayRadioGroup = useMemo(() => {
        return ![ComponentTypes.iprocIntegration, ComponentTypes.hrPerson].includes(component.type)
    }, [component])

    const onComponentSave = () => {
        onSave({
            ...component,
            protect: radioValue === "undefined" ? undefined : radioValue as externalFormProtectTypes
        })
        close();
    }

    return <div className={styles.container}>
        {displayRadioGroup && <Radiogroup vertical={false}
                    name="external-form-radio"
                    title={t("externalFormField")}
                    options={externalFormOptions}
                    onChange={setRadioValue}
                    value={radioValue}
        />}
        <div className={styles.supportText}>
            {component.type === ComponentTypes.file && <>{t("fileExternalFormText")}</>}
            {component.type === ComponentTypes.hrPerson && <>{t("hrPersonExternalFormText")}</>}
            {component.type === ComponentTypes.pdfPassword && <>{t("pdfPasswordExternalFormText")}</>}
            {component.type === ComponentTypes.iprocIntegration && <>{t("iprocIntegrationExternalFormText")}</>}
        </div>
        <div className={styles.buttonGroup}>
            <Button className={styles.button}
                    view="legal"
                    onClick={onComponentSave}>
                {displayRadioGroup ? t("save") : t("ok")}
            </Button>
        </div>
    </div>
});
